import { useNavigate, useLocation } from 'react-router-dom'

import { useAuth } from '@/auth/AuthContext'
import { useTheme } from '@/lib/ThemeContext'
import { THEME_LABELS, type ThemeName } from '@/lib/themes'

interface Props {
  onOpenPalette?: () => void
}

export function Masthead({ onOpenPalette }: Props) {
  const { user, logout } = useAuth()
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const isAdmin = user?.role === 'admin'
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

  return (
    <header className="shrink-0 bg-ink z-20 border-b border-haze/40">
      <div className="flex items-center justify-between h-16 px-6 lg:px-10 max-w-[1440px] mx-auto gap-4">
        <button
          onClick={() => navigate('/')}
          className="group flex items-baseline gap-2 text-cream transition-colors shrink-0"
        >
          <span className="font-display text-xl font-medium tracking-tight group-hover:text-ember transition-colors duration-300">
            beauty
          </span>
          <span className="font-display italic text-base text-ember/80 group-hover:text-ember transition-colors duration-300">
            one-oh-one
          </span>
        </button>

        {onOpenPalette && (
          <button
            onClick={onOpenPalette}
            className="group hidden sm:flex items-center gap-3 flex-1 max-w-sm px-4 py-2 bg-ink-raised border border-haze/60 rounded-full text-left hover:border-mute/60 transition-all duration-300"
            aria-label="Open command palette"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-mute">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
            </svg>
            <span className="text-sm text-mute flex-1">Search consultations…</span>
            <kbd className="font-mono text-[10px] text-mute border border-haze/60 px-1.5 py-0.5 rounded-md">
              {isMac ? '⌘K' : 'Ctrl K'}
            </kbd>
          </button>
        )}

        {onOpenPalette && (
          <button
            onClick={onOpenPalette}
            className="sm:hidden w-10 h-10 flex items-center justify-center text-mute border border-haze/60 rounded-full hover:border-ember hover:text-ember transition-all duration-300"
            aria-label="Open command palette"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
            </svg>
          </button>
        )}

        <div className="flex items-center gap-5 shrink-0">
          <div className="hidden md:flex items-center bg-ink-raised border border-haze/50 rounded-full overflow-hidden p-0.5">
            {(Object.keys(THEME_LABELS) as ThemeName[]).map((t) => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                className={`px-3 py-1 text-[11px] font-sans font-medium transition-all duration-300 rounded-full ${
                  theme === t
                    ? 'bg-cream text-ink shadow-sm'
                    : 'text-mute hover:text-cream'
                }`}
                title={`Switch to ${THEME_LABELS[t]}`}
              >
                {THEME_LABELS[t]}
              </button>
            ))}
          </div>

          {isAdmin && (
            <button
              onClick={() => navigate(location.pathname === '/admin' ? '/' : '/admin')}
              className="text-sm text-mute hover:text-cream transition-colors duration-300"
            >
              {location.pathname === '/admin' ? 'Chat' : 'Admin'}
            </button>
          )}

          <span className="text-sm text-mute hidden lg:block">
            {user?.email}
          </span>

          <button
            onClick={logout}
            className="text-sm text-mute hover:text-ember transition-colors duration-300"
            title="Sign out"
          >
            Sign out
          </button>
        </div>
      </div>
    </header>
  )
}
